import React, { useRef, useState } from "react";
import { Autocomplete, InputAdornment, TextField } from "@mui/material";
import { ProjectBudgetRange } from "ts-interfaces";
import { currencySymbol } from "../../../libs/currency";
import { StructuredFieldProps } from "./projectDetailFields";
import {
  BUDGET_RANGE_IDS,
  budgetDisplay,
  budgetEditValue,
  budgetPatchFromAmount,
  budgetPatchFromRange,
  budgetRangeLabel,
  parseBudgetInput,
  sanitizeBudgetInput,
} from "./projectBudget";

const isRangeId = (value: string): value is ProjectBudgetRange =>
  (BUDGET_RANGE_IDS as string[]).includes(value);

const LISTBOX_PROPS = { sx: { fontSize: "0.8rem" } };
const NO_FILTER = <T,>(options: T[]) => options;

/**
 * Validated budget input (#3787): type an exact amount or pick a range preset.
 * Junk is never persisted. While focused it shows raw digits (live grouping
 * would jump the caret); at rest, the formatted value — same convention as the
 * schedule editor's money cells.
 */
export const ProjectBudgetField = ({
  label,
  id,
  project,
  onChange,
  floatingLabel = false,
}: StructuredFieldProps) => {
  // Non-null only while the user is typing. null means "show the stored value".
  const [draft, setDraft] = useState<string | null>(null);
  // Blur commits only when the SANITIZED draft differs from what this edit
  // session started with. A keystroke flag isn't enough: a stored budgetRange
  // (or legacy non-numeric string) edits as "", and typed letters sanitize
  // back to "" — indistinguishable from a tab-through, but a keystroke-based
  // dirty bit would treat them as an intentional clear and wipe the range.
  const initialDraft = useRef("");
  const currency = project.currency;
  const displayed = draft ?? budgetDisplay(project);

  return (
    <Autocomplete<ProjectBudgetRange, false, false, true>
      freeSolo
      fullWidth
      id={id}
      size="small"
      options={BUDGET_RANGE_IDS}
      getOptionLabel={(option) =>
        isRangeId(option) ? budgetRangeLabel(option, currency) : option
      }
      // Always offer every preset — never filter them against a typed amount.
      filterOptions={NO_FILTER}
      value={null}
      inputValue={displayed}
      // Blur after a preset pick, so the next keystroke starts a fresh edit
      // session via onFocus instead of appending to the at-rest display label.
      blurOnSelect
      onFocus={() => {
        initialDraft.current = budgetEditValue(project);
        setDraft(initialDraft.current);
      }}
      onChange={(_, selected) => {
        // freeSolo also fires this with raw typed text on Enter.
        if (typeof selected !== "string" || !isRangeId(selected)) return;
        // Ref, not state: blurOnSelect's blur fires before the setDraft below
        // re-renders, and must not commit the stale draft over this range —
        // matching it to that stale draft makes the blur comparison a no-op.
        initialDraft.current = draft ?? "";
        setDraft(null);
        onChange(budgetPatchFromRange(selected, currency));
      }}
      onInputChange={(_, input, reason) => {
        if (reason !== "input") return;
        // Non-numeric characters never even reach the draft.
        setDraft(sanitizeBudgetInput(input));
      }}
      onBlur={() => {
        if (draft !== null && draft !== initialDraft.current) {
          const parsed = parseBudgetInput(draft);
          // false = junk: leave the stored value alone rather than writing it.
          if (parsed !== false)
            onChange(budgetPatchFromAmount(parsed, currency));
        }
        setDraft(null);
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={floatingLabel ? label : undefined}
          placeholder="180,000"
          // Spread, don't replace: params.inputProps carries the widget's ref/aria.
          inputProps={{
            ...params.inputProps,
            "aria-label": label,
            inputMode: "numeric",
          }}
          InputProps={{
            ...params.InputProps,
            // Only while editing — at rest budgetDisplay already carries it.
            startAdornment:
              draft !== null ? (
                <InputAdornment position="start">
                  {currencySymbol(currency)}
                </InputAdornment>
              ) : null,
          }}
        />
      )}
      ListboxProps={LISTBOX_PROPS}
    />
  );
};
